import React, { useEffect, useMemo, useState } from 'react';
import { Guide } from '@/pages/home/components/Guide';
import {
  getLocale,
  useDispatch,
  useIntl,
  useLocation,
  useSelector,
  history,
} from 'umi';

import {
  OPINION_GUIDE_BOX_LIST5,
  OPINION_GUIDE_HIDE,
} from '@/pages/home/components/GuideModule/GuideOpinionBox';
import {
  GUIDE_TYPE_OPINION_COMPETITOR,
  GUIDE_SYS_OPINION,
} from '@/pages/home/models/guide';
import { storage } from '@/utils/utils';

import game_switch_cn from './imgs/cn/game_switch_cn@2x.png';
import game_switch_en from './imgs/en/game_switch_en@2x.png';

const STEP1_PATH = '/search';
const STEP2_PATH = '/opinion/Overview';
const STEP3_PATH = '/opinion/Competitor';
const STEP_PATH_LIST = [STEP1_PATH, STEP2_PATH, STEP3_PATH];

import './index.scss';
import useOpinionGuideData from '@home/components/GuideModule/GuideOpinionBox/useOpinionGuideData';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
const GuideOverView = () => {
  const location = useLocation();
  const { pathname, query } = location;
  const { from } = query || {};
  const [currentGuideStep, setCurrentGuideStep] = useState(-3);
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEn = locale === 'en-US';
  const dispatch = useDispatch();
  const { guideType, userInfo, gameInfo } = useSelector((state: any) => {
    const {
      guide: { guideType },
      home: { userInfo },
      opinionCommon: { gameInfo },
    } = state;
    return {
      guideType,
      userInfo,
      gameInfo,
    };
  });

  const { excludeGuideSource } = useGetSingePageSourceRule({});
  const { GuideData } = useOpinionGuideData({
    gameInfo,
    userInfo,
    guidePermissions: excludeGuideSource,
  });

  const guideUsersHide = storage.getLocalStorageItem(OPINION_GUIDE_HIDE);

  // 注意下面两个useEffect不要合并到一起，不然会出现：从头部导航进入新手引导，点击到下一步时，关闭按钮无法关闭，而是返回到第一步
  useEffect(() => {
    // console.log('is GUIDE_TYPE_OPINION_COMPETITOR', pathname, STEP1_PATH);
    if (guideType === GUIDE_TYPE_OPINION_COMPETITOR) {
      if (
        pathname !== STEP1_PATH &&
        from !== 'userGuid_1' &&
        from !== 'userGuid_2'
      ) {
        return;
      }

      setCurrentGuideStep(
        from === 'userGuid_2' ? 2 : from === 'userGuid_1' ? 1 : 0,
      );
    }
  }, [guideType]);

  useEffect(() => {
    const list = storage.getLocalStorageItem(OPINION_GUIDE_BOX_LIST5) || [];
    const guideHide = storage.getLocalStorageItem(OPINION_GUIDE_HIDE);
    if (userInfo?.openid && !list.includes(userInfo.openid) && !guideHide) {
      if (
        pathname !== STEP1_PATH &&
        from !== 'userGuid_1' &&
        from !== 'userGuid_2'
      ) {
        return;
      }
      setCurrentGuideStep(
        from === 'userGuid_2' ? 2 : from === 'userGuid_1' ? 1 : 0,
      );
    }
  }, [userInfo]);

  const onFinished = () => {
    if (userInfo.openid) {
      const list = storage.getLocalStorageItem(OPINION_GUIDE_BOX_LIST5) || [];
      !list?.includes(userInfo.openid) && list.push(userInfo.openid);
      storage.setLocalStorageItem(OPINION_GUIDE_BOX_LIST5, list);
    }
    dispatch({
      type: 'guide/showOpinionGuideModule',
      payload: true,
    });
  };

  const onCloseStep = () => {
    dispatch({
      type: 'guide/save',
      payload: {
        guideSys: GUIDE_SYS_OPINION,
        guideType: '',
      },
    });
  };

  const onStepChange = (current: number) => {
    const { pathname } = location;
    if (current >= 0 && pathname !== STEP_PATH_LIST[current]) {
      let newPath = `${STEP_PATH_LIST[current]}?from=userGuid_${current}`;
      if (current === 0) {
        newPath += `&kw=pubg`;
      }

      history.push(newPath);
    }
    setCurrentGuideStep(current);
  };

  const subtitle = formatMessage({
    id: 'Competitor Information and Comparison',
  });
  const { blockList } = GuideData;
  const rank = blockList.findIndex((item) => item.tit === subtitle) + 1;
  const title = `${formatMessage({ id: 'Core Function' })} ${rank}`;

  // const rank = gameInfo?.entity_type !== 'mobile' ? 4 : 5;

  const steps = useMemo(() => {
    const steps = [
      {
        element: () => document.querySelector('.card-item'),
        title,
        subtitle,
        description: formatMessage({
          id: 'After searching the name of the competitor, click the button of the Opinions system to enter the Opinions homepage of the competitor.',
        }),
        placement: 'bottom',
        bubbleStyle: {},
      },
      {
        highlightElements: [
          {
            element: () => document.querySelector('.navmenu-hd-gameselector'),
          },
          // {
          //   element: () => document.querySelector('.dbo-gameselector-wrap'),
          //   hasRadius: true,
          // },
        ],
        element: () => document.querySelector('.dbo-competitor-card'),
        title,
        subtitle,
        description: formatMessage({
          id: 'You can switch the game in the side menu, or enter the main page of competitors from the competitor module.',
        }),
        placement: 'top-end',
        bubbleStyle: {},
        extImage: {
          image: isEn ? game_switch_en : game_switch_cn,
          placement: 'bottom-start',
          wrapperClassName: 'opinion-user-guide-competitor-game-selector-image',
          style: {},
        },
      },
      {
        element: () => document.querySelector('.header-first'),
        title,
        subtitle,
        description: formatMessage({
          id: 'Set competitors on the left side of the table, and check the comparison of opinion metrics and trends.',
        }),
        placement: 'right',
        bubbleStyle: {},
      },
    ];

    return steps;
  }, [currentGuideStep, locale]);

  if (guideType !== GUIDE_TYPE_OPINION_COMPETITOR) return null;

  return (
    // finishText 根据中英文调整
    <Guide
      current={currentGuideStep}
      onCurrentChange={(
        current: number,
        context: {
          from: 'start' | 'back' | 'next' | 'finish' | 'cancel' | 'nodata';
        },
      ) => {
        const { from } = context;
        setCurrentGuideStep(current);
        onStepChange(current);
        if (from === 'finish' || from === 'nodata') {
          onFinished();
        } else if (from === 'cancel') {
          onCloseStep();
          onFinished();
        }
      }}
      visibleCloseBtn
      hideCancelButton
      showBackButton
      nextButtonTheme="primary"
      backText={formatMessage({ id: 'guide_last_step' })}
      nextText={formatMessage({ id: 'guide_next_step' })}
      finishText={formatMessage({ id: 'guide_more_step' })}
      steps={steps}
      showLoading={false}
    />
  );
};

export default () => {
  const location = useLocation();

  return <>{STEP_PATH_LIST.includes(location.pathname) && <GuideOverView />}</>;
};
